/* FILE: ./frontend/src/features/admin/AdminDashboard.jsx */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase'; 
import RfqManager from '../canada/components/RfqManager';
import AnalyticsSuite from './components/AnalyticsSuite';
import SeoOrchestrator from './SeoOrchestrator';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'
});

export default function AdminDashboard() {
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('analytics'); 
  const [pendingSuppliers, setPendingSuppliers] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const settingsTabs = ['seo', 'users', 'compliance', 'oversight'];
  const isSettingActive = settingsTabs.includes(activeTab);

  const [editingBillingUser, setEditingBillingUser] = useState(null);
  const [billingFormData, setBillingFormData] = useState({ 
    supplier_tier: 'free', 
    buyer_tier: 'free', 
    connection_credits: 0,
    ai_audit_credits: 0,
    simulations_remaining: 3,
    sim_limit: 3,
    has_analytics: false,
    is_admin_overridden: false,
    supplier_expires_at: null,
    buyer_expires_at: null
  });
  const [billingUpdating, setBillingUpdating] = useState(false);

  const [globalRfqs, setGlobalRfqs] = useState([]);
  const [selectedGlobalRfq, setSelectedGlobalRfq] = useState(null);

  const [stats, setStats] = useState({
      users: 0,
      suppliers: 0,
      rfqs: 0,
      quotes: 0,
      revenueCredits: 0
  });

  const [hsRules, setHsRules] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [newHsRule, setNewHsRule] = useState({ rule_type: 'chapter_block', value: '', reason_message: '' });
  const [newKeyword, setNewKeyword] = useState({ keyword: '', severity: 'BLOCK' });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
        if (activeTab === 'analytics') {
            const res = await api.get('/analytics/admin/kpis');
            setStats({
                users: res.data.users || 0,
                suppliers: res.data.suppliers || 0,
                rfqs: res.data.rfqs || 0,
                quotes: res.data.quotes || 0,
                revenueCredits: res.data.revenueCredits || 0
            });
        }
        else if (activeTab === 'approvals') {
            const res = await api.get('/suppliers/pending');
            setPendingSuppliers(res.data || []);
        } else if (activeTab === 'users') {
            const res = await api.get('/admin/users');
            setSystemUsers(res.data || []);
        } else if (activeTab === 'compliance') {
            const { data: rulesData, error: rulesErr } = await supabase.from('compliance_rules').select('*').order('id', { ascending: false });
            if (rulesErr) throw rulesErr;
            setHsRules(rulesData || []);

            const { data: kwData, error: kwErr } = await supabase.from('military_keywords').select('*').order('id', { ascending: false });
            if (kwErr) throw kwErr;
            setKeywords(kwData || []);
        } else if (activeTab === 'oversight') {
            const res = await api.get('/admin/inquiries/all');
            const filteredRfqs = (res.data || []).filter(rfq => rfq.hs_code !== '000000' && rfq.type !== 'support');
            setGlobalRfqs(filteredRfqs);
        }
    } catch (err) {
        console.error(err);
        setError('Failed to load data. Ensure Backend is running and DB is accessible.');
    } finally {
        setLoading(false);
    }
  };

  const handleAddHsRule = async (e) => {
      e.preventDefault();
      try {
          const { data, error } = await supabase.from('compliance_rules').insert([{ ...newHsRule, category: 'STRICT_MILITARY' }]).select();
          if (error) throw error;
          setHsRules(prev => [data[0], ...prev]);
          setNewHsRule({ rule_type: 'chapter_block', value: '', reason_message: '' });
      } catch (err) { alert("Failed to add rule: " + err.message); }
  };

  const handleDeleteHsRule = async (id) => {
      if(!window.confirm("Delete this HS rule?")) return;
      try {
          const { error } = await supabase.from('compliance_rules').delete().eq('id', id);
          if (error) throw error;
          setHsRules(prev => prev.filter(r => r.id !== id));
      } catch (err) { alert("Failed to delete rule: " + err.message); }
  };

  const handleAddKeyword = async (e) => {
      e.preventDefault();
      try {
          const { data, error } = await supabase.from('military_keywords').insert([newKeyword]).select();
          if (error) throw error;
          setKeywords(prev => [data[0], ...prev]);
          setNewKeyword({ keyword: '', severity: 'BLOCK' });
      } catch (err) { alert("Failed to add keyword: " + err.message); }
  };

  const handleDeleteKeyword = async (id) => {
      if(!window.confirm("Delete this military keyword?")) return;
      try {
          const { error } = await supabase.from('military_keywords').delete().eq('id', id);
          if (error) throw error;
          setKeywords(prev => prev.filter(k => k.id !== id));
      } catch (err) { alert("Failed to delete keyword: " + err.message); }
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Confirm verification for this supplier?")) return;
    try {
      await api.patch(`/suppliers/${id}/verify`);
      alert("✅ Supplier Verified!");
      setPendingSuppliers(prev => prev.filter(s => s.id !== id));
      setSelectedSupplier(null);
    } catch (err) { alert("❌ Error: " + (err.response?.data?.error || err.message)); }
  };

  const handleReject = async (id) => {
      const reason = window.prompt("Why is this application being rejected? (This will be emailed to the user)");
      if (!reason) return; 

      try {
          await api.post(`/suppliers/${id}/reject`, { reason });
          alert("🚫 Supplier Rejected and Removed.");
          setPendingSuppliers(prev => prev.filter(s => s.id !== id));
          setSelectedSupplier(null);
      } catch (err) { alert("❌ Error rejecting: " + (err.response?.data?.error || err.message)); }
  };

  const handleKillSession = async (userId) => {
      if(!window.confirm("⚠️ Force Logout this user? They will be kicked out immediately.")) return;
      try {
          await api.post(`/admin/users/${userId}/kill`);
          alert("💀 Session Killed.");
          fetchData(); 
      } catch (err) { alert("Failed to kill session: " + (err.response?.data?.error || err.message)); }
  };

  const handleToggleBlock = async (userId, currentlyBlocked) => {
      const action = currentlyBlocked ? "UNBLOCK" : "SUSPEND";
      if(!window.confirm(`Are you sure you want to ${action} this user?`)) return;
      
      try {
          await api.post(`/admin/users/${userId}/toggle-block`, { block: !currentlyBlocked });
          alert(`User successfully ${currentlyBlocked ? 'unblocked' : 'suspended'}.`);
          fetchData(); 
      } catch (err) { alert(`Failed to toggle block: ` + (err.response?.data?.error || err.message)); }
  };

  const handleDeleteUser = async (userId, banUser = false) => {
      const msg = banUser ? "🚫 PERMANENTLY BAN & DELETE?" : "🗑️ STANDARD DELETE?";
      if(!window.confirm(msg)) return;
      
      try {
          await api.delete(`/admin/users/${userId}?banUser=${banUser}`);
          alert(banUser ? "User Banned & Deleted." : "User Deleted.");
          setSystemUsers(prev => prev.filter(u => u.id !== userId));
      } catch (err) { alert("Failed to delete: " + (err.response?.data?.error || err.message)); }
  };

  const handlePruneBots = async () => {
    if (!window.confirm("🤖 DELETE ALL BOT ACCOUNTS?")) return;
    setLoading(true);
    try {
        const bots = systemUsers.filter(u => u.email.startsWith('bot_') && u.email.includes('@test.com'));
        if (bots.length === 0) return alert("No bot accounts found.");
        for (const bot of bots) { await api.delete(`/admin/users/${bot.id}?banUser=false`); }
        alert(`✅ Cleaned up ${bots.length} bot accounts.`);
        fetchData(); 
    } catch (err) { alert("Cleanup failed: " + err.message); } finally { setLoading(false); }
  };

  const handleImpersonate = async (targetUser) => {
      if (!window.confirm(`🎭 Impersonate ${targetUser.email}? You will navigate the platform as this user.`)) return;
      try {
          const { data } = await api.post(`/admin/users/${targetUser.id}/impersonate`);
          localStorage.setItem('admin_recovery_token', localStorage.getItem('supabase.auth.token'));
          localStorage.setItem('supabase.auth.token', data.token);
          window.location.href = '/directory'; 
      } catch (err) {
          alert(`Failed to impersonate: ${err.response?.data?.error || err.message}`);
      }
  };

  const openBillingModal = async (u) => {
      setEditingBillingUser(u);
      setBillingUpdating(true);
      try {
          const { data } = await api.get(`/admin/users/${u.id}/billing`);
          setBillingFormData({
              supplier_tier: data.supplier_tier || 'free',
              buyer_tier: data.buyer_tier || 'free',
              connection_credits: data.connection_credits ?? 0,
              ai_audit_credits: data.ai_audit_credits ?? 0,
              simulations_remaining: data.simulations_remaining ?? 3,
              sim_limit: data.sim_limit ?? 3,
              has_analytics: Boolean(data.has_analytics),
              is_admin_overridden: Boolean(data.is_admin_overridden),
              supplier_expires_at: data.supplier_expires_at || null,
              buyer_expires_at: data.buyer_expires_at || null
          });
      } catch (err) {
          alert("Failed to load user billing data: " + (err.response?.data?.error || err.message));
          setEditingBillingUser(null);
      } finally {
          setBillingUpdating(false);
      }
  };

  const handleAdminSupplierTierChange = (newTier) => {
      setBillingFormData(prev => ({
          ...prev,
          supplier_tier: newTier,
          connection_credits: newTier === 'premium' ? 10 : 0
      }));
  };

  const handleAdminBuyerTierChange = (newTier) => {
      const defaultLimit = newTier === 'pro_volume' ? 300 : newTier === 'pro' ? 100 : 3;
      const defaultAudits = newTier === 'pro_volume' ? 300 : newTier === 'pro' ? 100 : 0;
      setBillingFormData(prev => ({
          ...prev,
          buyer_tier: newTier,
          sim_limit: defaultLimit,
          simulations_remaining: defaultLimit,
          ai_audit_credits: defaultAudits
      }));
  };

  const saveBillingUpdate = async (e) => {
      e.preventDefault();
      setBillingUpdating(true);
      try {
          await api.patch(`/admin/users/${editingBillingUser.id}/billing`, {
              supplier_tier: billingFormData.supplier_tier,
              buyer_tier: billingFormData.buyer_tier,
              connection_credits: parseInt(billingFormData.connection_credits, 10) || 0,
              ai_audit_credits: parseInt(billingFormData.ai_audit_credits, 10) || 0,
              simulations_remaining: parseInt(billingFormData.simulations_remaining, 10) || 0,
              has_analytics: billingFormData.has_analytics,
              is_admin_overridden: billingFormData.is_admin_overridden
          });
              
          alert("✅ User Subscription & Leftover Counters Updated Successfully!");
          setEditingBillingUser(null);
          fetchData();
      } catch (err) {
          alert("Update failed: " + (err.response?.data?.error || err.message));
      } finally {
          setBillingUpdating(false);
      }
  };

  const SupplierDetailModal = ({ supplier, onClose, onApprove, onReject }) => {
    if (!supplier) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center sticky top-0">
            <div>
                <h3 className="text-xl font-bold text-gray-900">{supplier.company_name}</h3>
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${supplier.compliance_tier?.includes('High Risk') ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                    {supplier.compliance_tier}
                </span>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">Business Identity</label>
                    <p className="text-sm font-medium">BN: {supplier.business_number || 'N/A'}</p>
                    <p className="text-sm">Est: {supplier.year_established}</p>
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">Contact</label>
                    <p className="text-sm">{supplier.contact_email}</p>
                    <p className="text-sm">{supplier.phone}</p>
                </div>
            </div>
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Location</label>
                <p className="text-sm bg-gray-50 p-2 rounded border">{supplier.address}, {supplier.city}, {supplier.province} {supplier.postal_code}</p>
            </div>
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Capabilities</label>
                <div className="flex flex-wrap gap-2 mt-1">
                    {supplier.capabilities?.map((cap, i) => (
                        <span key={i} className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs border border-blue-100">{cap}</span>
                    ))}
                </div>
            </div>
            
            {supplier.certification_url && (
                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                    <label className="text-xs font-bold text-amber-800 uppercase block mb-1">Uploaded Certificate</label>
                    <a href={supplier.certification_url} target="_blank" rel="noreferrer" className="text-sm font-bold text-blue-600 hover:underline flex items-center gap-1">
                        📄 View Document Attached
                    </a>
                </div>
            )}
            
            <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Description</label>
                <p className="text-sm text-gray-600 mt-1">{supplier.description}</p>
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-50 border-t flex justify-between gap-3 sticky bottom-0">
            <button onClick={() => onReject(supplier.id)} className="px-4 py-2 border border-red-300 text-red-600 font-bold rounded hover:bg-red-50">🚫 Reject</button>
            <div className="flex gap-3">
                <button onClick={onClose} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100">Cancel</button>
                <button onClick={() => onApprove(supplier.id)} className="px-6 py-2 bg-green-600 text-white font-bold rounded hover:bg-green-700 shadow-md">✓ Approve Supplier</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const AdminThreadViewer = ({ rfq, onClose }) => {
      if (!rfq) return null;
      return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full h-[85vh] flex flex-col overflow-hidden animate-fade-in-up">
                <div className="bg-slate-900 text-white px-6 py-4 flex justify-between items-center shrink-0">
                    <div>
                        <h3 className="font-bold text-lg">Platform Oversight Viewer</h3>
                        <div className="text-xs text-slate-300 mt-1">
                            Tracking Thread: {rfq.id}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">×</button>
                </div>
                
                <div className="bg-slate-100 border-b border-gray-200 p-4 shrink-0 flex gap-4 text-sm">
                    <div className="flex-1 bg-white p-3 rounded shadow-sm border border-gray-200">
                        <span className="block text-[10px] uppercase font-bold text-gray-400">Buyer</span>
                        <span className="font-bold text-gray-800">{rfq.buyer_email || 'Unknown Buyer'}</span>
                    </div>
                    <div className="flex-1 bg-white p-3 rounded shadow-sm border border-gray-200">
                        <span className="block text-[10px] uppercase font-bold text-gray-400">Supplier</span>
                        <span className="font-bold text-gray-800">{rfq.supplier_company || 'Unknown Supplier'}</span>
                    </div>
                    <div className="bg-white p-3 rounded shadow-sm border border-gray-200 shrink-0">
                        <span className="block text-[10px] uppercase font-bold text-gray-400">Status</span>
                        <span className={`font-bold ${rfq.status === 'accepted' ? 'text-green-600' : 'text-blue-600'}`}>
                            {rfq.status.toUpperCase()}
                        </span>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4">
                    <div className="text-center text-xs text-gray-400 my-4 border-b border-gray-200 leading-[0.1em]"><span className="bg-gray-50 px-2">Thread Start</span></div>
                    {rfq.messages?.map((m, i) => (
                        <div key={i} className={`flex ${m.sender_id === rfq.buyer_id ? 'justify-end' : 'justify-start'} my-2`}>
                            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                                m.sender_id === rfq.buyer_id 
                                ? 'bg-blue-100 text-blue-900 rounded-br-none border border-blue-200' 
                                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'
                            }`}>
                                <div className="text-[10px] font-bold mb-1 opacity-50 uppercase tracking-wide">
                                    {m.sender_id === rfq.buyer_id ? 'Buyer' : 'Supplier'}
                                </div>
                                <p className="whitespace-pre-wrap">{m.text}</p>
                                <p className="text-[10px] mt-2 text-right opacity-50">
                                    {new Date(m.created_at).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    ))}
                    {(!rfq.messages || rfq.messages.length === 0) && (
                        <div className="text-center text-gray-400 mt-10">No messages found in this thread.</div>
                    )}
                </div>
            </div>
        </div>
      );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        
        <div className="flex flex-col xl:flex-row justify-between items-center mb-8 gap-4">
          <h1 className="text-3xl font-bold text-gray-900">👮‍♂️ Admin Control Center</h1>
          
          <div className="flex bg-white p-1.5 rounded-xl shadow-sm border border-gray-200 gap-1 flex-wrap justify-center items-center">
             
             <button onClick={() => setActiveTab('analytics')} className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'analytics' ? 'bg-slate-800 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>📊 Analytics</button>
             <button onClick={() => setActiveTab('approvals')} className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'approvals' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>📝 Pending Approvals</button>
             <button onClick={() => setActiveTab('rfqs')} className={`px-4 py-2 text-sm font-bold rounded-lg transition-all ${activeTab === 'rfqs' ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}>📨 RFQ Concierge</button>
             
             <div className="relative border-l border-gray-200 pl-1 ml-1">
                <button 
                  onClick={() => setShowSettingsMenu(!showSettingsMenu)} 
                  className={`px-4 py-2 text-sm font-bold rounded-lg transition-all flex items-center gap-2 ${isSettingActive ? 'bg-slate-800 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'}`}
                >
                  ⚙️ System Settings <span className="text-[10px] opacity-60">{showSettingsMenu ? '▲' : '▼'}</span>
                </button>

                {showSettingsMenu && (
                    <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowSettingsMenu(false)}></div>
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 z-50 overflow-hidden py-1">
                            <button 
                                onClick={() => { setActiveTab('oversight'); setShowSettingsMenu(false); }} 
                                className={`w-full text-left px-4 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-gray-50 ${activeTab === 'oversight' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                            >
                                🕵️ Platform Oversight
                            </button>
                            <div className="border-t border-gray-100 my-1"></div>
                            <button 
                                onClick={() => { setActiveTab('seo'); setShowSettingsMenu(false); }} 
                                className={`w-full text-left px-4 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-gray-50 ${activeTab === 'seo' ? 'text-purple-600 bg-purple-50' : 'text-gray-700'}`}
                            >
                                🔍 SEO Discovery
                            </button>
                            <button 
                                onClick={() => { setActiveTab('users'); setShowSettingsMenu(false); }} 
                                className={`w-full text-left px-4 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-gray-50 ${activeTab === 'users' ? 'text-red-600 bg-red-50' : 'text-gray-700'}`}
                            >
                                👥 User Manager
                            </button>
                            <button 
                                onClick={() => { setActiveTab('compliance'); setShowSettingsMenu(false); }} 
                                className={`w-full text-left px-4 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-gray-50 ${activeTab === 'compliance' ? 'text-indigo-600 bg-indigo-50' : 'text-gray-700'}`}
                            >
                                🛡️ Compliance Engine
                            </button>
                        </div>
                    </>
                )}
             </div>

          </div>
        </div>

        {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">{error}</div>}

        <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden min-h-[500px]">
          
          {activeTab === 'analytics' && (
              <div className="bg-slate-50 min-h-full flex flex-col">
                  {loading ? <div className="text-center p-12 text-slate-400 animate-pulse">Calculating metrics...</div> : (
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 border-b border-slate-200 bg-white">
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                              <div className="text-slate-500 font-bold text-[10px] uppercase tracking-wider mb-1">Total Users</div>
                              <div className="text-2xl font-black text-slate-800">{stats.users}</div>
                          </div>
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                              <div className="text-slate-500 font-bold text-[10px] uppercase tracking-wider mb-1 flex justify-between">Suppliers <span>🏭</span></div>
                              <div className="text-2xl font-black text-blue-600">{stats.suppliers}</div>
                          </div>
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                              <div className="text-slate-500 font-bold text-[10px] uppercase tracking-wider mb-1 flex justify-between">Active RFQs <span>📨</span></div>
                              <div className="text-2xl font-black text-emerald-600">{stats.rfqs}</div>
                          </div>
                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                              <div className="text-slate-500 font-bold text-[10px] uppercase tracking-wider mb-1 flex justify-between">Quotes <span>💰</span></div>
                              <div className="text-2xl font-black text-yellow-600">{stats.quotes}</div>
                          </div>
                          <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 text-white flex flex-col justify-center">
                              <div className="text-slate-400 font-bold text-[10px] uppercase tracking-wider mb-1">Est. Credit Revenue</div>
                              <div className="text-2xl font-black text-emerald-400">${stats.revenueCredits?.toLocaleString() || 0}</div>
                          </div>
                      </div>
                  )}

                  <div className="flex-1 p-6">
                      <AnalyticsSuite />
                  </div>
              </div>
          )}

          {activeTab === 'approvals' && (
              <>
                <div className="px-6 py-4 border-b bg-gray-50 flex justify-between">
                    <h2 className="font-bold text-gray-700">Pending Approvals Queue</h2>
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-3 py-1 rounded-full">{pendingSuppliers.length} Waiting</span>
                </div>

                {loading ? <div className="p-12 text-center text-gray-500 animate-pulse">Loading...</div> : 
                pendingSuppliers.length === 0 ? <div className="p-16 text-center text-gray-400">All Caught Up!</div> : (
                    <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Tier</th>
                        <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Action</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {pendingSuppliers.map((s) => {
                            const isCertReview = s.compliance_tier === 'Tier 2 (High Risk)';
                            return (
                                <tr key={s.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedSupplier(s)}>
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-gray-900">{s.company_name}</div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${isCertReview ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-purple-100 text-purple-800 border border-purple-200'}`}>
                                                {isCertReview ? '📋 Cert Review' : '👤 Identity Review'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${s.compliance_tier?.includes('High') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                                            {s.compliance_tier}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <button onClick={(e) => { e.stopPropagation(); setSelectedSupplier(s); }} className="text-blue-600 hover:text-blue-900 font-bold text-sm">Review</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    </table>
                )}
              </>
          )}

          {activeTab === 'seo' && (
              <div className="p-6">
                <SeoOrchestrator />
              </div>
          )}

          {activeTab === 'users' && (
               <>
                <div className="px-6 py-4 border-b bg-gray-50 flex justify-between items-center">
                    <h2 className="font-bold text-gray-700">System Users (Active Sessions)</h2>
                    <div className="flex items-center gap-3">
                        <button onClick={handlePruneBots} className="bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1 rounded text-xs font-bold border border-red-200 transition-colors flex items-center gap-1">
                            <span>🧹</span> Prune Test Bots
                        </button>
                        <span className="bg-gray-200 text-gray-700 text-xs font-bold px-3 py-1 rounded-full">{systemUsers.length} Users</span>
                    </div>
                </div>

                {loading ? <div className="p-12 text-center text-gray-500 animate-pulse">Scanning Users...</div> : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">User Email</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Last Active</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">User ID</th>
                                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Control</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {systemUsers.map((u) => (
                            <tr key={u.id} className={`transition-colors ${u.is_blocked ? 'bg-red-50 opacity-75' : 'hover:bg-blue-50'}`}>
                                <td className="px-6 py-4">
                                    <div className="font-bold text-gray-900 flex items-center gap-2">
                                        {u.is_blocked && <span title="Account Suspended" className="text-red-600 text-lg">🛑</span>}
                                        {u.email}
                                    </div>
                                    <div className="text-xs text-gray-500">{u.provider}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                      {u.last_sign_in ? new Date(u.last_sign_in).toLocaleString() : 'Never'}
                                </td>
                                <td className="px-6 py-4 text-xs font-mono text-gray-400">
                                    {u.id}
                                </td>
                                <td className="px-6 py-4 text-right space-x-2 whitespace-nowrap">
                                    <button 
                                        onClick={() => handleImpersonate(u)} 
                                        className="bg-purple-100 text-purple-800 px-3 py-1 rounded text-xs font-bold hover:bg-purple-200 border border-purple-200"
                                        title="Log in as this user"
                                    >
                                        🎭 Impersonate
                                    </button>
                                    <button 
                                        onClick={() => openBillingModal(u)}
                                        className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded text-xs font-bold hover:bg-emerald-200 border border-emerald-200"
                                        title="Manage Tiers & Credits"
                                    >
                                        💳 Billing
                                    </button>
                                    <button onClick={() => handleToggleBlock(u.id, u.is_blocked)} className={`px-3 py-1 rounded text-xs font-bold ${u.is_blocked ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-orange-100 text-orange-800 hover:bg-orange-200'}`}>
                                        {u.is_blocked ? "✅ Unblock" : "⏸️ Suspend"}
                                    </button>
                                    <button onClick={() => handleKillSession(u.id)} className="bg-amber-100 text-amber-800 px-3 py-1 rounded text-xs font-bold hover:bg-amber-200">
                                        💀 Kill
                                    </button>
                                    <button onClick={() => handleDeleteUser(u.id, false)} className="bg-gray-100 text-gray-800 px-3 py-1 rounded text-xs font-bold hover:bg-gray-200">
                                        🗑️ Delete
                                    </button>
                                </td>
                            </tr>
                            ))}
                        </tbody>
                        </table>
                    </div>
                )}
               </>
          )}

          {activeTab === 'compliance' && (
              <>
                <div className="px-6 py-4 border-b bg-gray-50 flex justify-between items-center">
                    <h2 className="font-bold text-gray-700">Compliance Engine & Zero-Tolerance Policy</h2>
                    <span className="bg-indigo-100 text-indigo-800 text-xs font-bold px-3 py-1 rounded-full border border-indigo-200">Live Database Connect</span>
                </div>

                {loading ? <div className="p-12 text-center text-gray-500 animate-pulse">Loading Rules...</div> : (
                    <div className="p-6 bg-gray-50/50 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col max-h-[600px]">
                            <div className="p-5 border-b border-gray-100 bg-red-50/30">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                    <span className="text-xl">🚨</span> Restricted Keywords
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">Monitors product descriptions and chat messages.</p>
                            </div>
                            
                            <form onSubmit={handleAddKeyword} className="p-5 border-b border-gray-100 bg-gray-50 grid grid-cols-12 gap-3 items-end">
                                <div className="col-span-6">
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Keyword / Term</label>
                                    <input type="text" required placeholder="e.g. missile" value={newKeyword.keyword} onChange={e => setNewKeyword({...newKeyword, keyword: e.target.value})} className="w-full border-gray-300 rounded text-sm p-2 focus:ring-indigo-500" />
                                </div>
                                <div className="col-span-4">
                                    <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Action</label>
                                    <select value={newKeyword.severity} onChange={e => setNewKeyword({...newKeyword, severity: e.target.value})} className="w-full border-gray-300 rounded text-sm p-2 focus:ring-indigo-500">
                                        <option value="BLOCK">Hard Block</option>
                                        <option value="FLAG">Flag for Review</option>
                                    </select>
                                </div>
                                <div className="col-span-2">
                                    <button type="submit" className="w-full bg-indigo-600 text-white font-bold text-sm py-2 rounded hover:bg-indigo-700">+</button>
                                </div>
                            </form>

                            <div className="flex-1 overflow-y-auto p-2">
                                <ul className="divide-y divide-gray-100">
                                    {keywords.map(kw => (
                                        <li key={kw.id} className="p-3 flex justify-between items-center hover:bg-gray-50">
                                            <div>
                                                <span className="font-bold text-gray-800">{kw.keyword}</span>
                                                <span className={`ml-3 text-[10px] font-bold px-2 py-0.5 rounded uppercase ${kw.severity === 'BLOCK' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{kw.severity}</span>
                                            </div>
                                            <button onClick={() => handleDeleteKeyword(kw.id)} className="text-gray-400 hover:text-red-600 text-xl font-bold px-2">×</button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col max-h-[600px]">
                            <div className="p-5 border-b border-gray-100 bg-blue-50/30">
                                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                                    <span className="text-xl">🛑</span> HS Code Blocklist
                                </h3>
                                <p className="text-xs text-gray-500 mt-1">Blocks product listings based on Customs Chapters.</p>
                            </div>

                            <form onSubmit={handleAddHsRule} className="p-5 border-b border-gray-100 bg-gray-50 flex flex-col gap-3">
                                <div className="flex gap-3">
                                    <div className="flex-1">
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Target Type</label>
                                        <select value={newHsRule.rule_type} onChange={e => setNewHsRule({...newHsRule, rule_type: e.target.value})} className="w-full border-gray-300 rounded text-sm p-2 focus:ring-indigo-500">
                                            <option value="chapter_block">2-Digit Chapter</option>
                                            <option value="hs_prefix">4/6-Digit Prefix</option>
                                        </select>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Code Value</label>
                                        <input type="text" required placeholder="e.g. 93 or 88" value={newHsRule.value} onChange={e => setNewHsRule({...newHsRule, value: e.target.value})} className="w-full border-gray-300 rounded text-sm p-2 focus:ring-indigo-500" />
                                    </div>
                                </div>
                                <div className="flex gap-3 items-end">
                                    <div className="flex-[3]">
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Reason / Description</label>
                                        <input type="text" required placeholder="e.g. Arms and Ammunition" value={newHsRule.reason_message} onChange={e => setNewHsRule({...newHsRule, reason_message: e.target.value})} className="w-full border-gray-300 rounded text-sm p-2 focus:ring-indigo-500" />
                                    </div>
                                    <div className="flex-1">
                                        <button type="submit" className="w-full bg-indigo-600 text-white font-bold text-sm py-2 rounded hover:bg-indigo-700">Add Rule</button>
                                    </div>
                                </div>
                            </form>

                            <div className="flex-1 overflow-y-auto p-2">
                                <ul className="divide-y divide-gray-100">
                                    {hsRules.filter(r => r.rule_type === 'chapter_block' || r.rule_type === 'hs_prefix').map(rule => (
                                        <li key={rule.id} className="p-4 hover:bg-gray-50 flex justify-between items-start gap-4">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded text-xs">{rule.value}</span>
                                                    <span className="text-[10px] font-bold text-gray-400 uppercase">{rule.rule_type.replace('_', ' ')}</span>
                                                </div>
                                                <p className="text-sm text-gray-700">{rule.reason_message}</p>
                                            </div>
                                            <button onClick={() => handleDeleteHsRule(rule.id)} className="text-gray-400 hover:text-red-600 text-xl font-bold px-2">×</button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}
              </>
          )}

          {activeTab === 'rfqs' && (
              <div className="p-6">
                <RfqManager />
              </div>
          )}

          {activeTab === 'oversight' && (
               <>
                <div className="px-6 py-4 border-b bg-gray-50 flex justify-between items-center">
                    <h2 className="font-bold text-gray-700">Platform Oversight: Global RFQs</h2>
                    <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full border border-blue-200">{globalRfqs.length} Total RFQs</span>
                </div>

                {loading ? <div className="p-12 text-center text-gray-500 animate-pulse">Loading Platform Data...</div> : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Subject / Product</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Buyer</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Supplier</th>
                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {globalRfqs.map((rfq) => (
                            <tr key={rfq.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4">
                                    <div className="font-bold text-gray-900 line-clamp-1">{rfq.subject}</div>
                                    <div className="text-xs text-gray-500">{new Date(rfq.date || rfq.created_at).toLocaleDateString()}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                    {rfq.buyer_email || 'Unknown Buyer'}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-600">
                                    {rfq.supplier_company || 'Unknown Supplier'}
                                </td>
                                <td className="px-6 py-4">
                                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide ${
                                        rfq.status === 'accepted' ? 'bg-green-100 text-green-800' :
                                        rfq.status === 'replied' || rfq.has_quote ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {rfq.status}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <button 
                                        onClick={() => setSelectedGlobalRfq(rfq)}
                                        className="bg-white border border-gray-300 text-gray-700 px-3 py-1 rounded text-xs font-bold hover:bg-gray-50"
                                    >
                                        Spy 👁️
                                    </button>
                                </td>
                            </tr>
                            ))}
                        </tbody>
                        </table>
                        {globalRfqs.length === 0 && (
                            <div className="text-center p-12 text-gray-400">No RFQs have been submitted on the platform yet.</div>
                        )}
                    </div>
                )}
               </>
          )}

        </div>
      </div>

      {editingBillingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in-up">
                <div className="bg-emerald-50 px-6 py-4 border-b border-emerald-100 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-emerald-900 flex items-center gap-2"><span>💳</span> Manage Subscriptions & Counters</h3>
                    <button onClick={() => setEditingBillingUser(null)} className="text-emerald-400 hover:text-emerald-700 text-2xl">×</button>
                </div>
                
                {billingUpdating && !billingFormData.supplier_tier ? (
                     <div className="p-12 text-center text-emerald-600 animate-pulse font-bold">Loading current tier & balances...</div>
                ) : (
                    <form onSubmit={saveBillingUpdate} className="p-6 space-y-6">
                        <div>
                            <p className="text-sm text-gray-500 mb-4">Updating live subscription and leftover monthly balances for: <strong className="text-gray-900">{editingBillingUser.email}</strong></p>
                            
                            <div className="mb-4 bg-amber-50 text-amber-900 p-3 rounded-lg text-xs border border-amber-200 flex items-start gap-3">
                                <input
                                    id="admin_override_check"
                                    type="checkbox"
                                    className="mt-0.5 rounded border-amber-400 text-amber-600 focus:ring-amber-500 h-4 w-4"
                                    checked={billingFormData.is_admin_overridden}
                                    onChange={(e) => setBillingFormData({...billingFormData, is_admin_overridden: e.target.checked})}
                                />
                                <label htmlFor="admin_override_check" className="cursor-pointer select-none">
                                    <span className="font-bold block">🛡️ Lock Account Under Manual Admin Override</span>
                                    <span className="text-[11px] text-amber-800">Prevents automated Helcim cancellation webhooks or expiry sweeps from downgrading this user.</span>
                                </label>
                            </div>

                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Supplier Product Access</label>
                            <select 
                                value={billingFormData.supplier_tier}
                                onChange={(e) => handleAdminSupplierTierChange(e.target.value)}
                                className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500 mb-4"
                            >
                                <option value="free">Free Tier (Standard - 50 SKUs)</option>
                                <option value="premium">Premium Supplier ($199/mo - 10 RFQ Credits/mo + CAD + Analytics)</option>
                            </select>

                            <div className="mb-4">
                                <label className="inline-flex items-center cursor-pointer select-none">
                                    <input 
                                        type="checkbox"
                                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 h-4 w-4"
                                        checked={billingFormData.has_analytics}
                                        onChange={(e) => setBillingFormData({...billingFormData, has_analytics: e.target.checked})}
                                    />
                                    <span className="ml-2 text-sm font-bold text-gray-700 uppercase tracking-tight">Module A: Supplier Analytics ($99/mo)</span>
                                </label>
                                <p className="text-[10px] text-gray-400 ml-6 mt-1">Unmasks profile visitor dashboard and search metrics.</p>
                            </div>

                            <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Buyer Tool Access</label>
                            <select 
                                value={billingFormData.buyer_tier}
                                onChange={(e) => handleAdminBuyerTierChange(e.target.value)}
                                className="w-full border-gray-300 rounded-lg p-3 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                            >
                                <option value="free">Free Tier (3 Simulations/mo, 0 Deep Audits)</option>
                                <option value="pro">Pro Buyer Standard ($99/mo - 100 Sims & 100 AI Audits)</option>
                                <option value="pro_volume">Pro Buyer High Volume ($199/mo - 300 Sims & 300 AI Audits)</option>
                            </select>
                        </div>

                        <div className="pt-4 border-t border-gray-100">
                            <h4 className="text-xs font-black text-gray-900 mb-1 uppercase tracking-wide">Current Cycle Leftover Counters (No Rollover)</h4>
                            <p className="text-[11px] text-gray-500 mb-3">Shows the exact remaining runs for this user's active cycle. Modify any value to adjust their live balance.</p>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-700 uppercase tracking-wide mb-1.5">RFQ Credits Left</label>
                                    <input 
                                        type="number" 
                                        min="0"
                                        value={billingFormData.connection_credits}
                                        onChange={(e) => setBillingFormData({...billingFormData, connection_credits: e.target.value})}
                                        className="w-full border-gray-300 rounded-lg p-2.5 text-sm focus:ring-emerald-500 focus:border-emerald-500 font-mono text-base"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">1 per engaged RFQ.</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-indigo-700 uppercase tracking-wide mb-1.5">Simulations Left</label>
                                    <input 
                                        type="number" 
                                        min="0"
                                        value={billingFormData.simulations_remaining}
                                        onChange={(e) => setBillingFormData({...billingFormData, simulations_remaining: e.target.value})}
                                        className="w-full border-gray-300 rounded-lg p-2.5 text-sm focus:ring-indigo-500 focus:border-indigo-500 font-mono text-base"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Of {billingFormData.sim_limit}/mo cap.</p>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-blue-700 uppercase tracking-wide mb-1.5">AI Audits Left</label>
                                    <input 
                                        type="number" 
                                        min="0"
                                        value={billingFormData.ai_audit_credits}
                                        onChange={(e) => setBillingFormData({...billingFormData, ai_audit_credits: e.target.value})}
                                        className="w-full border-gray-300 rounded-lg p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500 font-mono text-base"
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Deep Compliance.</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4 border-t border-gray-100">
                            <button type="button" onClick={() => setEditingBillingUser(null)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100 w-1/3 text-sm font-bold transition-colors">Cancel</button>
                            <button type="submit" disabled={billingUpdating} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded hover:bg-emerald-700 shadow-md w-2/3 disabled:opacity-50 text-sm transition-all">
                                {billingUpdating ? 'Saving...' : 'Save Billing Changes'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
      )}

      {selectedGlobalRfq && (
          <AdminThreadViewer rfq={selectedGlobalRfq} onClose={() => setSelectedGlobalRfq(null)} />
      )}

      {selectedSupplier && (
        <SupplierDetailModal 
            supplier={selectedSupplier} 
            onClose={() => setSelectedSupplier(null)} 
            onApprove={handleApprove} 
            onReject={handleReject}
        />
      )}
    </div>
  );
}